/**
 * Every page of a paged list, a few pages at a time instead of one after another.
 *
 *   fetchPage(page) → { items, totalPages, total }
 *
 * The first page says how many there are; the rest go out `concurrency` at a time and are put back
 * in page order, so the result is exactly what a walk from page 1 to the last would return. With
 * a `cap`, only the pages needed to reach it are asked for.
 */
export async function fetchAllPages(fetchPage, { concurrency = 4, cap = Infinity, onProgress } = {}) {
  const first = await fetchPage(1);
  const firstItems = first.items || [];
  const totalPages = Math.max(1, Number(first.totalPages) || 1);
  const total = first.total ?? firstItems.length;
  const pageSize = firstItems.length || 1;
  const lastPage = Number.isFinite(cap) ? Math.min(totalPages, Math.max(1, Math.ceil(cap / pageSize))) : totalPages;

  const pages = new Array(lastPage);
  pages[0] = firstItems;
  let loaded = firstItems.length;
  onProgress?.(loaded, total);

  let next = 2;
  const worker = async () => {
    while (next <= lastPage) {
      const page = next;
      next += 1;
      const res = await fetchPage(page);
      pages[page - 1] = res.items || [];
      loaded += pages[page - 1].length;
      onProgress?.(loaded, total);
    }
  };
  const workers = Math.max(1, Math.min(concurrency, lastPage - 1));
  await Promise.all(Array.from({ length: workers }, worker));

  const items = pages.flat();
  const capped = Number.isFinite(cap) ? items.slice(0, cap) : items;
  return { items: capped, total, truncated: capped.length < total };
}

export default fetchAllPages;
